import React, { useRef, useState, useEffect } from "react";
import api, { fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtGbp } from "@/lib/constants";
import { X, Plus, Trash2, GripVertical, Check, ChevronDown } from "lucide-react";

const emptyLine = () => ({ account_code: "", account_name: "", description: "", debit: "", credit: "", tracking_option: "" });

function AccountCombobox({ line, accounts, onPick, testid }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);
    useEffect(() => {
        const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);
    const display = line.account_code ? `${line.account_code} · ${line.account_name}` : (line.account_name || "");
    const ql = q.trim().toLowerCase();
    const filtered = accounts.filter((a) => !ql || `${a.code} ${a.name}`.toLowerCase().includes(ql)).slice(0, 200);
    return (
        <div ref={wrapRef} className="relative">
            <div className="flex items-center border border-input rounded-sm bg-white">
                <input
                    data-testid={testid}
                    value={open ? q : display}
                    onFocus={() => { setOpen(true); setQ(""); }}
                    onChange={(e) => { setQ(e.target.value); if (!open) setOpen(true); }}
                    placeholder="— account —"
                    className="w-full px-1 py-1 bg-transparent outline-none placeholder:text-muted-foreground"
                    autoComplete="off"
                />
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground mr-1 shrink-0" />
            </div>
            {open && (
                <div className="absolute z-[70] left-0 top-full mt-0.5 w-[260px] max-h-56 overflow-auto bg-white border border-border rounded-sm shadow-xl" data-testid={`${testid}-list`}>
                    {filtered.length === 0 && <div className="px-2 py-1.5 text-muted-foreground">No matching accounts</div>}
                    {filtered.map((a) => (
                        <button
                            key={(a.code || "") + "|" + a.name}
                            type="button"
                            data-testid={`${testid}-opt-${a.code || a.name}`}
                            onClick={() => { onPick(a); setOpen(false); setQ(""); }}
                            className="block w-full text-left px-2 py-1 hover:bg-brand/10"
                        >
                            {a.code ? <span className="font-num text-muted-foreground">{a.code} · </span> : null}{a.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export function JournalDialog({ engagementId, engagement, origin, journal, accounts = [], schedules = [], trackingName, onClose, onSaved }) {
    const editing = !!journal;
    const [narration, setNarration] = useState(journal?.narration || "");
    const [date, setDate] = useState((journal?.date || engagement?.current_period_end || "").slice(0, 10));
    const [originId, setOriginId] = useState(journal?.origin_wp_id || origin?.id || "");
    const [lines, setLines] = useState(() => (journal?.lines?.length ? journal.lines.map((l) => ({ ...l, debit: l.debit || "", credit: l.credit || "" })) : [emptyLine(), emptyLine()]));
    const [busy, setBusy] = useState(false);
    const [pos, setPos] = useState({ x: Math.max(40, window.innerWidth - 820), y: 90 });
    const drag = useRef(null);

    const originCode = origin?.code || journal?.origin_wp_code || "";
    const originName = origin?.title || journal?.origin_wp_name || "";

    const onDown = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y }; window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp); };
    const onMove = (e) => { if (!drag.current) return; setPos({ x: Math.max(0, drag.current.ox + e.clientX - drag.current.sx), y: Math.max(0, drag.current.oy + e.clientY - drag.current.sy) }); };
    const onUp = () => { drag.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };

    const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
    const pickAccount = (i, a) => setLine(i, { account_code: a.code || "", account_name: a.name || "" });
    const addLine = () => setLines((ls) => [...ls, emptyLine()]);
    const delLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

    const totDr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const totCr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    const balanced = Math.abs(totDr - totCr) < 0.01 && totDr > 0;

    const save = async () => {
        const payload = {
            narration, date, origin_wp_id: originId || null,
            lines: lines.filter((l) => l.account_code || parseFloat(l.debit) || parseFloat(l.credit)).map((l) => ({
                account_code: l.account_code, account_name: l.account_name, description: l.description,
                debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0,
                tracking_name: l.tracking_option ? (trackingName || "Tracking") : null, tracking_option: l.tracking_option || null,
            })),
        };
        if (!payload.lines.length) return toast.error("Add at least one journal line with an account and amount");
        setBusy(true);
        try {
            if (editing) await api.put(`/engagements/${engagementId}/journals/${journal.id}`, payload);
            else await api.post(`/engagements/${engagementId}/journals`, payload);
            toast.success(editing ? "Journal updated" : "Journal saved to F5");
            onSaved?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed z-[60] w-[780px] max-w-[95vw] bg-white border border-brand/30 rounded-md shadow-2xl" style={{ left: pos.x, top: pos.y }} data-testid="journal-dialog">
            <div className="flex items-center gap-2 px-3 py-2 bg-brand text-white rounded-t-md cursor-move select-none" onMouseDown={onDown} data-testid="journal-dialog-header">
                <GripVertical className="w-4 h-4 opacity-70" />
                <span className="font-display font-semibold text-sm">{editing ? `Edit journal ${journal.ref}` : "New journal"}</span>
                {(originCode || journal) && <span className="text-[11px] bg-white/15 px-1.5 py-0.5 rounded-sm">{editing ? journal.ref : "auto J-number"}{originCode ? ` · ${originCode}` : ""}</span>}
                <button onClick={onClose} className="ml-auto hover:bg-white/15 rounded-sm p-0.5" data-testid="journal-dialog-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 space-y-3 max-h-[75vh] overflow-auto">
                <div className="text-[11px] text-muted-foreground bg-slate-50 border border-border rounded-sm px-2 py-1.5" data-testid="journal-description-preview">
                    Description: <span className="font-medium text-foreground">{editing ? journal.ref : "J###"} – {originCode || "—"} – {originName || "—"} – {narration || <span className="italic text-muted-foreground">your narration…</span>}</span>
                </div>
                <div className="grid grid-cols-[1fr_150px] gap-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-display">Reason / narration</label>
                        <input data-testid="journal-narration" value={narration} onChange={(e) => setNarration(e.target.value)} placeholder="e.g. accrue Q4 cleaning invoice" className="w-full border border-input rounded-sm px-2 py-1.5 text-sm bg-white mt-0.5" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-display">Date</label>
                        <input data-testid="journal-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-input rounded-sm px-2 py-1.5 text-sm bg-white mt-0.5" />
                    </div>
                </div>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            <th className="text-left font-display font-semibold pb-1">Account</th>
                            <th className="text-left font-display font-semibold pb-1">Line description</th>
                            <th className="text-right font-display font-semibold pb-1 w-24">Debit</th>
                            <th className="text-right font-display font-semibold pb-1 w-24">Credit</th>
                            <th className="text-left font-display font-semibold pb-1 w-32">Tracking</th>
                            <th className="w-6"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((l, i) => (
                            <tr key={i} data-testid={`journal-line-${i}`}>
                                <td className="pr-1 py-0.5">
                                    <AccountCombobox line={l} accounts={accounts} onPick={(a) => pickAccount(i, a)} testid={`journal-line-account-${i}`} />
                                </td>
                                <td className="pr-1 py-0.5"><input data-testid={`journal-line-desc-${i}`} value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="(defaults to journal description)" className="w-full border border-input rounded-sm px-1 py-1 bg-white" /></td>
                                <td className="pr-1 py-0.5"><input data-testid={`journal-line-debit-${i}`} type="number" step="0.01" value={l.debit} onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? "" : l.credit })} className="w-full border border-input rounded-sm px-1 py-1 bg-white text-right font-num" /></td>
                                <td className="pr-1 py-0.5"><input data-testid={`journal-line-credit-${i}`} type="number" step="0.01" value={l.credit} onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? "" : l.debit })} className="w-full border border-input rounded-sm px-1 py-1 bg-white text-right font-num" /></td>
                                <td className="pr-1 py-0.5">
                                    <select data-testid={`journal-line-tracking-${i}`} value={l.tracking_option || ""} onChange={(e) => setLine(i, { tracking_option: e.target.value })} className="w-full border border-input rounded-sm px-1 py-1 bg-white">
                                        <option value="">—</option>
                                        {schedules.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td className="py-0.5 text-center">{lines.length > 1 && <button data-testid={`journal-line-remove-${i}`} onClick={() => delLine(i)} className="text-muted-foreground/60 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                            </tr>
                        ))}
                        <tr className="border-t border-border font-semibold">
                            <td colSpan={2} className="py-1 text-right pr-2 text-muted-foreground uppercase text-[10px] tracking-widest">Totals</td>
                            <td className="text-right font-num" data-testid="journal-total-debit">{fmtGbp(totDr)}</td>
                            <td className="text-right font-num" data-testid="journal-total-credit">{fmtGbp(totCr)}</td>
                            <td colSpan={2}></td>
                        </tr>
                    </tbody>
                </table>
                <div className="flex items-center gap-3">
                    <button data-testid="journal-add-line" onClick={addLine} className="flex items-center gap-1 text-xs text-brand hover:underline"><Plus className="w-3.5 h-3.5" /> Add line</button>
                    <span data-testid="journal-balance-badge" className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm ${balanced ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {balanced ? "Balanced" : `Out of balance by ${fmtGbp(Math.abs(totDr - totCr))}`}
                    </span>
                    {!balanced && <span className="text-[11px] text-amber-700">Saves as <b>not posted</b> — it won't feed the accounts until it balances.</span>}
                </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-3 py-2.5 border-t border-border bg-slate-50 rounded-b-md">
                <button onClick={onClose} className="text-xs border border-border hover:bg-white px-3 py-1.5 rounded-sm" data-testid="journal-cancel">Cancel</button>
                <button onClick={save} disabled={busy} className="text-xs bg-brand hover:bg-brand-hover text-white px-4 py-1.5 rounded-sm font-medium flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="journal-save"><Check className="w-3.5 h-3.5" /> {editing ? "Update journal" : "Save journal"}</button>
            </div>
        </div>
    );
}
